import { useCallback, useEffect, useState } from "react";

import { GenerateForm } from "./components/GenerateForm";
import { QrResult, type Result } from "./components/QrResult";
import { ApiError, fetchConfig, fetchTotal, generate, type ServerConfig } from "./lib/api";
import { describeDuration, ttlOptions } from "./lib/ttl";

export function App() {
  const [url, setUrl] = useState("");
  const [usage, setUsage] = useState("");
  const [ttl, setTtl] = useState("");
  const [size, setSize] = useState("");

  const [config, setConfig] = useState<ServerConfig | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // The server decides which lifetimes it can issue, so the form is built from
  // its answer rather than from assumptions about the deployed backend.
  useEffect(() => {
    let active = true;

    fetchConfig()
      .then((loaded) => {
        if (active) setConfig(loaded);
      })
      .catch(() => {
        // The form still works with its default option; no need to alarm anyone.
      });

    return () => {
      active = false;
    };
  }, []);

  const refreshTotal = useCallback(() => {
    fetchTotal()
      .then(setTotal)
      .catch(() => setTotal(null));
  }, []);

  useEffect(refreshTotal, [refreshTotal]);

  async function submit() {
    setPending(true);
    setError(null);

    try {
      const chosenSize = size ? Number(size) : null;

      const generated = await generate({
        url: url.trim(),
        usage: usage.trim(),
        ...(ttl ? { ttl } : {}),
        ...(chosenSize !== null ? { size: chosenSize } : {}),
      });

      setResult({
        imageUrl: generated.imageUrl,
        shortUrl: generated.shortUrl,
        targetUrl: generated.targetUrl,
        expiry: describeExpiry(ttl, config),
        size: chosenSize ?? config?.defaultSize ?? null,
      });
      refreshTotal();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Something went wrong. Please try again.");
      setResult(null);
    } finally {
      setPending(false);
    }
  }

  return (
    <main>
      <header className="masthead">
        <h1>QR Code Generator</h1>
        <p>Turn a link into a QR code. The code holds a short link, so it stays easy to scan.</p>
      </header>

      <GenerateForm
        url={url}
        usage={usage}
        ttl={ttl}
        size={size}
        ttlOptions={ttlOptions(config)}
        sizes={config?.sizes ?? []}
        defaultSize={config?.defaultSize ?? null}
        pending={pending}
        onUrlChange={setUrl}
        onUsageChange={setUsage}
        onTtlChange={setTtl}
        onSizeChange={setSize}
        onSubmit={submit}
      />

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {result && <QrResult result={result} />}

      <footer>{describeTotal(total)}</footer>
    </main>
  );
}

/**
 * Renders the running total. A non-breaking space holds the line's height
 * while the count is still loading, so the page does not shift under the user.
 */
function describeTotal(total: number | null): string {
  if (total === null) return "\u00a0";
  if (total === 1) return "1 code generated so far";
  return `${total.toLocaleString()} codes generated so far`;
}

/** Puts the chosen lifetime into words for the result panel. */
function describeExpiry(ttl: string, config: ServerConfig | null): string {
  if (ttl === "never") return "Never";
  if (ttl) return `After ${describeDuration(ttl)}`;
  if (config) return `After ${describeDuration(config.defaultTtl)}`;
  return "After the default period";
}
